import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetAllFiles,
  formatNotebookDate,
  filesOrFoldersKeys,
  FileOrFolder,
  NotebookFile,
  NotebookFolder,
  FileScopeType,
} from 'components/FileManagement/hooks/useGetAllFiles';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import IconEmptyExercise from 'assets/images/icon__exercise-details-empty.svg';
import isRoleReadOnly from 'PREPAREsrc/utils/isRoleReadOnly';
import { useFileProcessingStatus } from './hooks/useFileProcessingStatus';
import { useReprocessAllFiles } from './hooks/fileMutations';
import UploadFilesButton from './UploadFilesButton';
import FileActionsMenu from './FileActionsMenu';
import FolderActionsMenu from './FolderActionsMenu';
import AddFolderButton from './AddFolderButton';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import ExpandArrow from '../DesignSystems/buttons/ExpandArrow';
import RAGBadge from './RAGBadge';
import { FileReadinessProgress } from './FileReadinessProgress';

type TreeNode = {
  id: string;
  name: string;
  isFolder: boolean;
  children: TreeNode[];
  parentId: string | null;
  level: number;
  data: FileOrFolder;
  isExpanded?: boolean;
  childrenLoaded?: boolean;
};

const compareTreeNodes = (a: TreeNode, b: TreeNode) => {
  if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
  return a.name.localeCompare(b.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

type TreeViewProps = {
  ownerId: string;
  userWorkspaceRoleName?: string;
  // Scope of the workspace (e.g. 'organization' for the org file library). Threaded into the
  // list query and upload/create-folder actions so the backend can enforce org permissions.
  scopeType?: FileScopeType;
  // Selection mode props
  selectionMode?: boolean;
  selectedFileIds?: string[];
  onSelectionChange?: (fileIds: string[]) => void;
  showUploadButton?: boolean;
  showCreateFolderButton?: boolean;
  // When true, suppress the built-in search bar. Useful when a parent component drives
  // search externally (e.g. ChatFileSelector renders a single shared search across sections).
  hideSearch?: boolean;
  // When provided, overrides the internal search state. Pair with hideSearch=true.
  externalSearchTerm?: string;
  // When provided, only the contents of this folder are shown (the folder itself is the root).
  rootFolderId?: string;
};

const FilesTreeView: React.FC<TreeViewProps> = ({
  ownerId,
  userWorkspaceRoleName,
  scopeType,
  selectionMode = false,
  selectedFileIds = [],
  onSelectionChange,
  showUploadButton = true,
  showCreateFolderButton = true,
  hideSearch = false,
  externalSearchTerm,
  rootFolderId,
}) => {
  const [internalSearch, setInternalSearch] = useState('');
  const search = externalSearchTerm ?? internalSearch;
  const [expandedFolders, setExpandedFolders] = useState<
    Record<string, boolean>
  >({});

  // Use the new hook to fetch all files and folders
  const query = useGetAllFiles(ownerId, scopeType);
  const { refetch } = query;
  const items = query.data?.dataset;

  // Distinguish files that are genuinely processing from ones whose RAG job
  // was lost (e.g. a server restart mid-processing). Only polls while at least
  // one file is unprocessed.
  const hasUnprocessedFiles = useMemo(
    () =>
      (items ?? []).some(
        (item) => item.type === 'file' && !(item as NotebookFile).rag_ready
      ),
    [items]
  );
  const processingStatusQuery = useFileProcessingStatus(
    ownerId,
    scopeType,
    hasUnprocessedFiles
  );
  const processingStates = processingStatusQuery.data;
  const stuckCount = useMemo(
    () =>
      Object.values(processingStates ?? {}).filter((state) => state === 'stuck')
        .length,
    [processingStates]
  );
  const reprocessAllFiles = useReprocessAllFiles();
  const readOnly = isRoleReadOnly(userWorkspaceRoleName);
  const queryClient = useQueryClient();

  // The status endpoint only returns not-yet-indexed files, so a file the
  // (non-polling) list still shows as unprocessed that is ABSENT from a
  // fresher status response must have finished processing — refresh the list
  // so its badge flips to Ready without a page reload. Optimistic upload rows
  // (temp- ids) are skipped so this can't fight the upload mutation's cache.
  useEffect(() => {
    if (!processingStates || !items) return;
    // Only trust status data fetched after the list; once the list refetches,
    // this stops firing until the next poll (no invalidation loop).
    if (processingStatusQuery.dataUpdatedAt <= query.dataUpdatedAt) return;

    const someFileBecameReady = items.some(
      (item) =>
        item.type === 'file' &&
        !(item as NotebookFile).rag_ready &&
        !item.id.startsWith('temp-') &&
        !(item.id in processingStates)
    );
    if (someFileBecameReady) {
      queryClient.invalidateQueries({
        queryKey: filesOrFoldersKeys.allFiles(ownerId),
        exact: false,
      });
    }
  }, [
    processingStates,
    items,
    processingStatusQuery.dataUpdatedAt,
    query.dataUpdatedAt,
    ownerId,
    queryClient,
  ]);

  // Build tree from items - memoized to avoid unnecessary recalculation
  const treeData = useMemo(() => {
    if (!items) return [];

    const buildTree = (items: FileOrFolder[]) => {
      const nodes: Record<string, TreeNode> = {};
      const rootNodes: TreeNode[] = [];
      items.forEach((item) => {
        const isFolder = item.type === 'folder';
        const node: TreeNode = {
          id: item.id,
          name: item.folder_name || item.file_name || '',
          isFolder,
          children: [],
          parentId: item.notefolderId || null,
          level: 0,
          data: item,
          isExpanded: expandedFolders[item.id] || false,
          childrenLoaded: false,
        };
        nodes[item.id] = node;
      });
      Object.values(nodes).forEach((node) => {
        // what if the parentId isn't read in yet... we should cache it and add it later?
        // Or sort properly? Or use a different datastructure? Or search for children?
        if (node.parentId && nodes[node.parentId]) {
          nodes[node.parentId].children.push(node);
        } else {
          rootNodes.push(node);
        }
      });
      rootNodes.sort(compareTreeNodes);
      Object.values(nodes).forEach((node) =>
        node.children.sort(compareTreeNodes)
      );
      if (rootFolderId) {
        return nodes[rootFolderId]?.children ?? [];
      }
      return rootNodes;
    };

    return buildTree(items);
  }, [items, expandedFolders, rootFolderId]);

  // Flatten tree for rendering - memoized to avoid recalculation on search changes
  const flattenedTree = useMemo(() => {
    const flattenedItems: TreeNode[] = [];
    const flattenTree = (nodes: TreeNode[], level: number) => {
      nodes.forEach((node) => {
        const nodeWithLevel = { ...node, level };
        flattenedItems.push(nodeWithLevel);
        if (
          node.isFolder &&
          expandedFolders[node.id] &&
          node.children.length > 0
        ) {
          flattenTree(node.children, level + 1);
        }
      });
    };
    flattenTree(treeData, 0);
    return flattenedItems;
  }, [treeData, expandedFolders]);

  // Apply search filter - memoized to only recalculate when search or flattenedTree changes
  const filteredTree = useMemo(
    () =>
      search
        ? flattenedTree.filter((node) =>
            node.name.toLowerCase().includes(search.toLowerCase())
          )
        : flattenedTree,
    [search, flattenedTree]
  );

  const toggleFolder = (folderId: string) => {
    // Toggle expanded state
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const handleFileAction = (actionType: string, _file: FileOrFolder) => {
    if (actionType === 'delete') {
      refetch();
    }
  };

  const handleFolderAction = (actionType: string, _folder: FileOrFolder) => {
    if (['delete', 'move'].includes(actionType)) {
      refetch();
    }
  };

  // Selection mode helpers
  const getAllChildFileIds = (node: TreeNode): string[] => {
    const fileIds: string[] = [];
    const traverse = (n: TreeNode) => {
      if (!n.isFolder) {
        fileIds.push(n.id);
      }
      n.children.forEach(traverse);
    };
    traverse(node);
    return fileIds;
  };

  const getFolderCheckState = (
    node: TreeNode
  ): 'checked' | 'unchecked' | 'indeterminate' => {
    if (!node.isFolder) return 'unchecked';
    const childFileIds = getAllChildFileIds(node);
    if (childFileIds.length === 0) return 'unchecked';
    const selectedCount = childFileIds.filter((id) =>
      selectedFileIds.includes(id)
    ).length;
    if (selectedCount === 0) return 'unchecked';
    if (selectedCount === childFileIds.length) return 'checked';
    return 'indeterminate';
  };

  const handleCheckboxChange = (node: TreeNode, checked: boolean) => {
    if (!onSelectionChange) return;

    if (node.isFolder) {
      // Toggle all child files
      const childFileIds = getAllChildFileIds(node);
      const newSelection = checked
        ? [...new Set([...selectedFileIds, ...childFileIds])]
        : selectedFileIds.filter((id) => !childFileIds.includes(id));
      onSelectionChange(newSelection);
    } else {
      // Toggle single file
      const newSelection = checked
        ? [...selectedFileIds, node.id]
        : selectedFileIds.filter((id) => id !== node.id);
      onSelectionChange(newSelection);
    }
  };

  return (
    <div className={selectionMode ? 'px-3 pb-2' : 'p-4'}>
      {!hideSearch && (
        <div className="col-12 mb-4">
          <div className="d-flex flex-column flex-lg-row align-items-center">
            <div className="d-flex align-items-center justify-content-between ms-0 w-75">
              <StylishNewSearchBar
                className={'w-100'}
                value={search}
                onChangeSearchTerm={setInternalSearch}
              />
            </div>
          </div>
        </div>
      )}

      {!selectionMode && !readOnly && stuckCount > 1 && (
        <div className="d-flex align-items-center justify-content-between bg-warning bg-opacity-10 mb-3 rounded p-2">
          <span>
            ${stuckCount} files are not being processed and will not appear in
            searches.
          </span>
          <StylishNewButton
            className="button--primary ms-3"
            disabled={reprocessAllFiles.isPending}
            onClick={() =>
              reprocessAllFiles.mutate({ workspaceId: ownerId, scopeType })
            }
          >
            <SharedIcon iconName="refresh" classes="me-2" size="sm" />
            Re-run all
          </StylishNewButton>
        </div>
      )}

      {filteredTree.length > 0 ? (
        <div className="tree-view-container">
          <table className="table">
            <thead>
              <tr>
                {selectionMode && <th style={{ width: '40px' }}></th>}
                <th>Name</th>
                {!selectionMode && <th>Last Modified</th>}
                <th style={{ minWidth: '160px' }}>Status</th>
                {!selectionMode && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredTree.map((node) => {
                const isFileSelected =
                  !node.isFolder && selectedFileIds.includes(node.id);
                const folderCheckState = node.isFolder
                  ? getFolderCheckState(node)
                  : 'unchecked';

                return (
                  <tr key={node.id}>
                    {selectionMode && (
                      <td>
                        <input
                          type="checkbox"
                          checked={
                            node.isFolder
                              ? folderCheckState === 'checked'
                              : isFileSelected
                          }
                          ref={(el) => {
                            if (
                              el &&
                              node.isFolder &&
                              folderCheckState === 'indeterminate'
                            ) {
                              el.indeterminate = true;
                            }
                          }}
                          onChange={(e) =>
                            handleCheckboxChange(node, e.target.checked)
                          }
                          aria-label={`Select ${node.name}`}
                          className="form-check-input"
                        />
                      </td>
                    )}
                    <td style={{ paddingLeft: `${node.level * 24}px` }}>
                      <div className="d-flex align-items-center">
                        {node.isFolder ? (
                          <>
                            <ExpandArrow
                              toggle={() => toggleFolder(node.id)}
                              isCollapsed={!node.isExpanded}
                            />
                            <SharedIcon iconName={'folder'} />
                          </>
                        ) : (
                          <>
                            <div style={{ width: '24px' }}></div>
                            <SharedIcon iconName={'description'} />
                          </>
                        )}
                        <span className="ms-2">{node.name}</span>
                        {node.isFolder &&
                        (node.data as NotebookFolder).position_name ? (
                          <span
                            className="badge bg-secondary ms-2"
                            title="Files here are listed on this position's tasks"
                          >
                            {(node.data as NotebookFolder).position_name}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    {!selectionMode && (
                      <td>{formatNotebookDate(node.data.updatedAt)}</td>
                    )}
                    <td>
                      {!node.isFolder && (
                        <div className="d-flex flex-column gap-2">
                          <FileReadinessProgress
                            file={node.data as NotebookFile}
                          />
                          <RAGBadge
                            file={node.data as NotebookFile}
                            ownerId={ownerId}
                            processingState={processingStates?.[node.data.id]}
                            readOnly={readOnly}
                          />
                        </div>
                      )}
                    </td>
                    {!selectionMode && (
                      <td>
                        {node.isFolder ? (
                          <FolderActionsMenu
                            folder={node.data}
                            ownerId={ownerId}
                            scopeType={scopeType}
                            onFolderAction={handleFolderAction}
                          />
                        ) : (
                          <FileActionsMenu
                            file={node.data}
                            onFileAction={handleFileAction}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : selectionMode ? (
        <div className="px-2 py-2 text-center">
          <small className="text-muted fst-italic">No files yet</small>
        </div>
      ) : query.isLoading ? (
        <div>Loading...</div>
      ) : (
        <div className="bg-gray-800 p-5 text-center">
          <div className="py-md-4 py-0">
            <div className="d-flex justify-content-center mb-4">
              <img src={IconEmptyExercise} alt="" />
            </div>
            <p className="weight-600 mb-4">
              {selectionMode
                ? 'No files uploaded yet. Upload files to get started.'
                : 'A fresh start. Create the first entry.'}
            </p>
            {!isRoleReadOnly(userWorkspaceRoleName) && (
              <div className="d-flex flex-column flex-md-row justify-content-center">
                {showUploadButton && (
                  <UploadFilesButton
                    ownerId={ownerId}
                    userWorkspaceRoleName={userWorkspaceRoleName}
                    className="button--primary"
                    showNotificationText={false}
                    showTooltip={false}
                    scopeType={scopeType}
                  />
                )}
                {showCreateFolderButton && (
                  <AddFolderButton
                    ownerId={ownerId}
                    className="button--primary mt-md-0 ms-md-2 mt-2"
                    scopeType={scopeType}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilesTreeView;
